import { ReactNode } from "react";
import { Box, Typography, Paper, Button, Chip } from "@mui/material";
import type { GrowerInfo, JobCardInfo } from "./types";
import { EntityCardMenu } from "./EntityCardMenu";

interface MobileEntityListProps<TEntity extends { id: string }> {
	entities: TEntity[];
	growersByEntity: Map<string, GrowerInfo[]>;
	jobsByEntity: Map<string, JobCardInfo[]>;
	selectedItems: Set<string>;
	assignSelectedToEntity: (entityId: string) => void;
	assignAllToEntity: (entityId: string) => void;
	assignUnassignedToEntity: (entityId: string) => void;
	unassignAllFromEntity: (entityId: string) => void;
	renderEntityHeader: (entity: TEntity) => ReactNode;
	headerBar?: ReactNode;
	emptyMessage: string;
	/** Extra content rendered after the entity list (e.g., AddSalespersonCard) */
	footer?: ReactNode;
	/** Called after a successful "Assign Selected" tap — used to switch tab back */
	onAfterAssign?: () => void;
}

export function MobileEntityList<TEntity extends { id: string }>({
	entities,
	growersByEntity,
	jobsByEntity,
	selectedItems,
	assignSelectedToEntity,
	assignAllToEntity,
	assignUnassignedToEntity,
	unassignAllFromEntity,
	renderEntityHeader,
	headerBar,
	emptyMessage,
	footer,
	onAfterAssign,
}: MobileEntityListProps<TEntity>) {
	return (
		<Box sx={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }}>
			{headerBar && <Box sx={{ mb: 1 }}>{headerBar}</Box>}

			<Box sx={{ flex: 1, overflowY: "auto", minHeight: 0 }}>
				{entities.length === 0 && (
					<Typography variant="body2" color="text.secondary" sx={{ textAlign: "center", mt: 2 }}>
						{emptyMessage}
					</Typography>
				)}

				{entities.map((entity) => {
					const assignedGrowers = growersByEntity.get(entity.id) || [];
					const assignedJobs = jobsByEntity.get(entity.id) || [];
					const totalAssigned = assignedGrowers.length + assignedJobs.length;

					return (
						<Paper key={entity.id} variant="outlined" sx={{ p: 1.5, mb: 1 }}>
							{/* Entity header content (step-specific) */}
							<Box sx={{ display: "flex", alignItems: "flex-start", mb: 0.5 }}>
								<Box sx={{ flex: 1 }}>{renderEntityHeader(entity)}</Box>
								<Chip
									label={`${totalAssigned} assigned`}
									size="small"
									color={totalAssigned > 0 ? "primary" : "default"}
									variant="outlined"
									sx={{ ml: 0.5, flexShrink: 0 }}
								/>
								<EntityCardMenu
									entityId={entity.id}
									onAssignAll={assignAllToEntity}
									onAssignUnassigned={assignUnassignedToEntity}
									onUnassignAll={unassignAllFromEntity}
									selectedCount={selectedItems.size}
									onAssignSelected={assignSelectedToEntity}
								/>
							</Box>

							{/* Assigned summary chips */}
							{totalAssigned > 0 && (
								<Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mb: 0.5 }}>
									{assignedGrowers.map((g) => (
										<Chip key={g.name} label={g.name} size="small" variant="outlined" sx={{ fontSize: "0.7rem" }} />
									))}
									{assignedJobs.map((j) => (
										<Chip
											key={j.jobId}
											label={j.fieldName || "Unnamed"}
											size="small"
											variant="outlined"
											sx={{ fontSize: "0.7rem" }}
										/>
									))}
								</Box>
							)}

							{/* Assign selected button */}
							{selectedItems.size > 0 && (
								<Button
									size="small"
									variant="contained"
									fullWidth
									onClick={() => {
										assignSelectedToEntity(entity.id);
										onAfterAssign?.();
									}}
									sx={{ mt: 0.5 }}
								>
									Assign Selected ({selectedItems.size})
								</Button>
							)}
						</Paper>
					);
				})}

				{footer}
			</Box>
		</Box>
	);
}
